'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { read, utils, type WorkSheet } from 'xlsx'
import xlsxCalc from 'xlsx-calc'
import Papa from 'papaparse'
import { FileUpload, Toast } from '@skop-app/lib-front'
import { formatFileSize } from '@skop-app/lib-domain/utils'

// Utility functions
const hasMergedCells = (sheet: WorkSheet) => {
  return sheet['!merges'] && sheet['!merges'].length > 0
}

const isRowEmpty = (row: readonly unknown[]) => {
  return row.every((cell) => {
    if (cell === null || cell === undefined) return true
    if (typeof cell === 'string') return cell.trim() === ''
    return false
  })
}

const showErrorAndCleanup = (message: string, deleteCurrent: () => void) => {
  Toast.error(message, {
    cancelAutoClose: true,
    closeButton: true,
  })
  setTimeout(() => {
    deleteCurrent()
  }, 4000)
}

// Validation functions
const validateData = (
  headers: string[],
  rows: any[],
  maxRows: number,
  t: (key: string, params?: any) => string,
) => {
  // Check if the sheet contains no data
  if (headers.length === 0 || rows.length === 0) {
    throw new Error(t('FileUploader.errors.noData'))
  }

  // Check if the sheet contains more than the maximum number of rows
  if (rows.length > maxRows) {
    throw new Error(t('FileUploader.errors.tooManyRows', { maxRows }))
  }

  // Check if the sheet contains duplicate columns
  if (hasDuplicateHeaders(headers)) {
    throw new Error(t('FileUploader.errors.duplicateColumns'))
  }
}

// CSV processing
export const normalizeHeader = (h: string): string =>
  String(h ?? '')
    .trim()
    .replace(/\s+/g, ' ')

export const isAllowedHeader = (normalized: string): boolean =>
  normalized !== '' && !/^_\d+$/.test(normalized)

export const hasDuplicateHeaders = (headers: readonly string[]): boolean => {
  const normalizedHeaders = headers
    .map((header) => normalizeHeader(header))
    .filter((header) => header !== '')
  return new Set(normalizedHeaders).size !== normalizedHeaders.length
}

export const getAllowedCsvHeaders = (
  rawHeaders: readonly unknown[],
): string[] =>
  rawHeaders
    .map((header) => String(header ?? ''))
    .filter((header) => isAllowedHeader(normalizeHeader(header)))

const processCsvFile = async (
  file: File,
  t: (key: string, params?: any) => string,
): Promise<{ headers: string[]; rows: any[] }> =>
  new Promise((resolve, reject) => {
    if (!file.text) {
      reject(new Error(t('FileUploader.errors.cannotReadCsv')))
      return
    }

    file.text().then((text) => {
      const preview = Papa.parse<string[]>(text, {
        header: false,
        preview: 1,
        skipEmptyLines: true,
      })

      const previewHeaderRow = Array.isArray(preview.data[0])
        ? preview.data[0]
        : []
      if (previewHeaderRow.length === 0) {
        reject(new Error(t('FileUploader.errors.noData')))
        return
      }

      if (hasDuplicateHeaders(getAllowedCsvHeaders(previewHeaderRow))) {
        reject(new Error(t('FileUploader.errors.duplicateColumns')))
        return
      }

      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.data.length === 0) {
            reject(new Error(t('FileUploader.errors.noData')))
            return
          }

          const allowedHeaders = getAllowedCsvHeaders(previewHeaderRow)

          const rows = (results.data as Record<string, unknown>[]).map(
            (row) => {
              const out: Record<string, unknown> = {}
              for (const h of allowedHeaders) {
                out[h] = row[h]
              }
              return out
            },
          )

          const filteredRows = rows.filter(
            (row) => !isRowEmpty(Object.values(row)),
          )

          resolve({ headers: allowedHeaders, rows: filteredRows })
        },
        error: () => {
          reject(new Error(t('FileUploader.errors.csvReadError')))
        },
      })
    })
  })

// Excel formula processing
const processHyperlinkFormulas = (workbook: any) => {
  for (const sheetName of Object.keys(workbook.Sheets)) {
    const sheet = workbook.Sheets[sheetName]
    if (sheet['!ref']) {
      const range = utils.decode_range(sheet['!ref'])
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = utils.encode_cell({ r: row, c: col })
          const cell = sheet[cellAddress]

          if (cell?.f?.includes('HYPERLINK')) {
            const match = cell.f.match(
              /HYPERLINK\s*\(\s*["']([^"']*)["']\s*,\s*["']([^"']*)["']\s*\)/i,
            )
            if (match) {
              cell.v = match[2] // Use the display text
              cell.t = 's' // Mark as string
            }
          }
        }
      }
    }
  }
}

// Excel processing
const processExcelFile = async (
  file: File,
  t: (key: string, params?: any) => string,
): Promise<{ headers: string[]; rows: any[] }> => {
  const data = await file.arrayBuffer()
  const workbook = read(data, {
    cellFormula: true,
    cellNF: true,
    cellText: true,
  })

  // Add support for HYPERLINK function
  if (!xlsxCalc.functions?.HYPERLINK) {
    xlsxCalc.functions = xlsxCalc.functions || {}
    xlsxCalc.functions.HYPERLINK = (link, text) => text || link
  }

  // Try to calculate formulas, handle unsupported functions gracefully
  try {
    xlsxCalc(workbook)
  } catch (calcError) {
    console.warn(
      'Some Excel formulas could not be calculated:',
      calcError.message,
    )
    processHyperlinkFormulas(workbook)
  }

  // Find the sheet to read the data from (skip "À lire" if a data sheet exists)
  const readMeIndex = workbook.SheetNames.indexOf('À lire')
  const dataIndex = readMeIndex === 0 && workbook.SheetNames.length > 1 ? 1 : 0
  const sheet = workbook.Sheets[workbook.SheetNames[dataIndex]]
  if (!sheet) {
    throw new Error(t('FileUploader.errors.noData'))
  }

  // Check if the sheet contains merged cells
  if (hasMergedCells(sheet)) {
    throw new Error(t('FileUploader.errors.mergedCells'))
  }

  const sheetHeaders = utils.sheet_to_json(sheet, {
    header: 1,
  })[0] as string[]

  const headers = sheetHeaders
  const rows = utils
    .sheet_to_json(sheet)
    .filter((row) => !isRowEmpty(Object.values(row as Record<string, unknown>)))

  return { headers, rows }
}

/**
 * FileUploader allows to upload a file, check its integrity and parse it to extract data.
 * Should replace the old FileUpload component from @skop-app/lib-front
 * @param callback - Function to pass to receive the data extracted from the file (columns, lines)
 * @param maxRows - Maximum number of rows to accept
 */
export const FileUploader = ({
  callback,
  maxRows = 5000,
  maxWeight = 10000000,
  disabled,
}) => {
  const t = useTranslations()
  const [file, setFile] = useState(null)

  const deleteCurrent = () => setFile(null)

  const setData = async (file: File) => {
    if (!file) return

    try {
      let headers: string[] = []
      let rows: any[] = []

      // Process file based on type
      if (file.type.includes('csv')) {
        const result = await processCsvFile(file, t)
        headers = result.headers
        rows = result.rows
      } else {
        const result = await processExcelFile(file, t)
        headers = result.headers
        rows = result.rows
      }

      // Validate the extracted data
      validateData(headers, rows, maxRows, t)

      // If all validations pass, call the callback
      callback(headers, rows)
    } catch (error) {
      console.error('FileUploader error:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('FileUploader.errors.generalError')
      showErrorAndCleanup(errorMessage, deleteCurrent)
    }
  }

  const maxSize = formatFileSize(maxWeight)

  return (
    <FileUpload
      setData={setData}
      file={file}
      setFile={setFile}
      deleteCurrent={deleteCurrent}
      toast={Toast}
      accept={{
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.oasis.opendocument.spreadsheet, application/vnd.ms-excel, text/csv',
        message: t('FileUploader.accept.message'),
        size: maxWeight,
        sizeMessage: t('FileUploader.accept.sizeMessage', { maxSize }),
        defaultMessage: t('FileUploader.accept.defaultMessage', { maxSize }),
      }}
      className="flex size-full flex-col items-center justify-center"
      disabled={disabled}
    />
  )
}
